import React, { useState, useRef, useEffect } from 'react';
import './ProfileImageUpload.css';

const ProfileImageUpload = ({ 
  currentImage, 
  onImageChange, 
  name = 'Profile Image',
  disabled = false 
}) => {
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Update preview when currentImage changes
  useEffect(() => {
    if (currentImage) {
      const imageUrl = currentImage.startsWith('http') 
        ? currentImage 
        : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${currentImage}`;
      setPreview(imageUrl);
    } else {
      setPreview(null);
    }
  }, [currentImage]);

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
        onImageChange(file, e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onImageChange(null, null);
  };

  const getInitials = (fullName) => {
    return fullName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="profile-image-upload">
      <label className="form-label">Profile Image</label>
      
      <div className="image-upload-container">
        <div className="image-preview-section">
          <div className="image-preview">
            {preview ? (
              <img src={preview} alt="Profile preview" className="preview-image" />
            ) : (
              <div className="preview-placeholder">
                <span className="placeholder-initials">
                  {name ? getInitials(name) : 'PI'}
                </span>
              </div>
            )}
          </div>
          
          {preview && (
            <button
              type="button"
              className="remove-image-btn"
              onClick={handleRemove}
              disabled={disabled}
              title="Remove image"
            >
              ×
            </button>
          )}
        </div>

        <div 
          className={`upload-area ${dragOver ? 'drag-over' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="upload-content">
            <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            <p className="upload-text">
              Drop image here or click to browse
            </p>
            <p className="upload-hint">
              Supports: JPG, PNG, GIF (Max 5MB)
            </p>
          </div>

          <div className="upload-buttons">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="file-input"
              id="profile-image-file"
              disabled={disabled}
            />
            <label 
              htmlFor="profile-image-file" 
              className="btn-upload"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              Browse Files
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileImageUpload;